import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, LoaderCircle } from "lucide-react";
import AgentRail, { ASKMILA_KEY } from "./AgentRail";
import { ASKMILA_PATH, getAgentKeyForPath, getAgentPath, getAgentView } from "./agentRegistry";
import { useAgentAccess } from "../context/AgentAccessContext";
import { ONBOARDING_STATUS, useOnboarding } from "../context/OnboardingContext";
import { CurrentUser } from "../../business/helper/CurrentUser";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import MilaContainer from "../MilaContainer";
import UserSettingsModal from "../specialized/UserSettingsModal";

/** How long the Settings entry pulses after onboarding completes. */
const SETTINGS_SPOTLIGHT_MS = 15000;

/**
 * Pulses the Settings control for a moment after onboarding completes, so the user sees where
 * the preferences Mila just learned can be changed. Held here rather than in the chat because
 * the control it lights is on the rail — the chrome shared by every view — and the onboarding
 * conversation may well have ended with the user somewhere other than the chat.
 */
const useSettingsSpotlight = () => {
  const { status } = useOnboarding();
  const [isLit, setIsLit] = useState(false);
  const previousStatusRef = useRef(null);

  useEffect(() => {
    const previous = previousStatusRef.current;
    previousStatusRef.current = status;
    if (
      previous &&
      previous !== ONBOARDING_STATUS.Completed &&
      status === ONBOARDING_STATUS.Completed
    ) {
      setIsLit(true);
      const timer = setTimeout(() => setIsLit(false), SETTINGS_SPOTLIGHT_MS);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const extinguish = useCallback(() => setIsLit(false), []);

  return [isLit, extinguish];
};

/**
 * A backend-registered agent with no client module yet. Rendering a tile rather than a broken
 * route is what lets the backend ship an agent before the frontend does (§13.1).
 */
const ComingSoon = ({ agent }) => (
  <div className="flex h-full items-center justify-center p-10">
    <div className="max-w-md rounded-2xl bg-whitesmoke px-8 py-10 text-center font-notosans">
      <h2 className="mb-2 text-[18px] font-bold text-dark-blue-100">
        {agent?.displayName || "This agent"}
      </h2>
      <p className="text-[14px] leading-6 text-dimgray-200">
        {agent?.description ||
          "This agent is available to you, but its screen has not shipped yet."}
      </p>
    </div>
  </div>
);

const Spinner = () => (
  <div className="flex h-full items-center justify-center">
    <LoaderCircle className="animate-spin text-dark-blue-100" size={28} />
  </div>
);

/**
 * The application shell (design §13.1).
 *
 * Wraps the existing AskMila screen in the AI services rail without changing any of its logic:
 * the only touches on existing code are that AskMila's own side panel now sits inboard of the
 * rail, and that its floating Central / sign-out button yields to the entry at the foot of the
 * rail whenever the rail is on screen.
 *
 * A user entitled to no agents gets no rail at all — the screen is byte-for-byte what they see
 * today, which is what makes the shell safe to put in front of everybody.
 *
 * <b>The URL is the view.</b> A rail entry is a link rather than a tab: AskMila is `/mila` and
 * an agent is its own address (`/incident-prevention-agent`), so the screen on show can be
 * bookmarked, sent to a colleague, typed by hand, and — the one that matters most — reloaded.
 * This used to be a piece of component state, which meant every refresh put a champion halfway
 * through their day back into the chat with no way to say where they had been.
 *
 * <b>The rail is on every width; below `lg` it is a drawer.</b> It used to be desktop-only,
 * because AskMila's own side panel is an overlay there and a second standing left-hand chrome
 * would have fought it — but hiding the navigation also took the agent away with it: narrowing
 * the window dropped a champion back into the chat mid-task, and there was no way back in. So on
 * a narrow screen the rail keeps its whole content and none of its footprint: it rests off-screen
 * and is opened from one button in the top-right corner, where AskMila's own Central button used
 * to sit (that button, and the sidebar's logo and gear, stand down whenever the rail is present —
 * on every width now, so the rail is the one place chrome lives).
 */
const AppShell = ({ handleLogout, isFromCentral = false }) => {
  const { agents, isLoading, hasFailed } = useAgentAccess();
  const isDesktop = useIsDesktop();

  // Surfaced once, in the console, because a rail that is absent because the entitlement call
  // never succeeded looks exactly like a rail that is absent because you have no agents.
  useEffect(() => {
    if (hasFailed) {
      console.warn(
        "[AppShell] GET /Agents/Mine never resolved, so the AI services rail is hidden. " +
          "This is not the same as having no agents — check the backend and reload."
      );
    }
  }, [hasFailed]);

  // AskMila is always the landing view, entitled agents or not: it is the front door of the
  // product, and an agent is somewhere you choose to go. `/mila` is therefore also what every
  // address the shell cannot place resolves to, and the rail opens with AskMila selected, so
  // the highlight matches what is on screen from the first frame.
  const location = useLocation();
  const navigate = useNavigate();
  const routeKey = getAgentKeyForPath(location.pathname) ?? ASKMILA_KEY;

  // Deliberately not gated on `isLoading`: `agents` is empty until it resolves anyway, and
  // including it made every later refresh blink the rail out and back. Not gated on the width
  // either: below `lg` the rail changes shape (AgentRail), it does not go away — a champion who
  // narrows the window keeps both the agent they are on and the way out of it.
  const hasRail = agents.length > 0;

  // Only below `lg`, where the rail rests off-screen. Nothing holds it open on the desktop side:
  // there the rail is always on the strip and opens on approach.
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  // Widening the window puts the rail back on the strip, so a drawer left open would be an
  // overlay with nothing behind it and a scrim over the whole screen.
  useEffect(() => {
    if (isDesktop) setIsDrawerOpen(false);
  }, [isDesktop]);

  // Choosing somewhere to go is also done with the drawer, so the drawer's work is over.
  // A plain push rather than a replace: moving between AI services is history, and Back should
  // walk it the way it walks any other navigation.
  const handleSelect = useCallback(
    (key) => {
      navigate(key === ASKMILA_KEY ? ASKMILA_PATH : getAgentPath(key));
      setIsDrawerOpen(false);
    },
    [navigate]
  );

  // Signing out is not a bare `handleLogout`: AskMila owns a choreography around it — clear
  // the conversation, drop the server-side request context, then log out. `MilaContainer`
  // registers that flow here and the rail's sign-out entry runs it, from whichever view the
  // user is on (the chat stays mounted behind an agent, so the flow is always live). The
  // fallback covers only the frames before registration, where a plain logout is the whole
  // of what a click can mean anyway.
  const signOutRef = useRef(null);
  const registerSignOut = useCallback((fn) => {
    signOutRef.current = fn;
  }, []);
  const handleRailSignOut = useCallback(() => {
    setIsDrawerOpen(false);
    (signOutRef.current || handleLogout)();
  }, [handleLogout]);

  // The profile settings ("what Mila knows about you") are one profile for the whole product,
  // read by AskMila and by every agent, so the shell owns the modal and every door to it: the
  // rail's Settings entry, and the gear AskMila's sidebar keeps for the screens with no rail.
  // Owned here rather than by the chat for a concrete reason too — the chat is `display:none`
  // behind an agent, and a modal rendered inside it would be invisible from the agent's screen.
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSettingsHighlighted, extinguishSettingsSpotlight] = useSettingsSpotlight();
  const handleOpenSettings = useCallback(() => {
    extinguishSettingsSpotlight();
    setIsDrawerOpen(false);
    setIsSettingsOpen(true);
  }, [extinguishSettingsSpotlight]);
  const handleCloseSettings = useCallback(() => setIsSettingsOpen(false), []);

  // Whether the address can be honoured. An agent the user has no grant for — and a path that
  // names no agent at all — falls back to the chat, which also covers the grant revoked while
  // the page was open: the rail entry disappears, and so does the view.
  //
  // Never while the entitlement call is in flight, though. `agents` is empty until
  // `GET /Agents/Mine` answers, and that window is most of a cold load — precisely the reload
  // of a bookmarked agent, where falling back to the chat would be the bug rather than the fix.
  // So an agent address is taken on trust until the call has actually answered, retries and
  // all, and only then settled.
  const isEntitled = agents.some((agent) => agent.agentKey === routeKey);
  const effectiveKey =
    routeKey === ASKMILA_KEY || isEntitled || isLoading ? routeKey : ASKMILA_KEY;

  // Where the view on screen ought to be addressed from, and the whole of the navigation the
  // shell does on its own. Anything the address bar got wrong settles here: a path naming
  // nothing, an agent that is no longer granted, `/accident-prevention` typed from the stored
  // key rather than the agent's name, a stray capital. `replace`, because an address that was
  // never a place is not somewhere Back should return to.
  const canonicalPath =
    effectiveKey === ASKMILA_KEY ? ASKMILA_PATH : getAgentPath(effectiveKey);

  useEffect(() => {
    if (location.pathname === canonicalPath) return;
    navigate(
      // Only the path was wrong; whatever else the address carried belongs to the same view.
      { pathname: canonicalPath, search: location.search, hash: location.hash },
      { replace: true }
    );
  }, [location.pathname, location.search, location.hash, canonicalPath, navigate]);

  const activeAgent = useMemo(
    () => agents.find((agent) => agent.agentKey === effectiveKey) || null,
    [agents, effectiveKey]
  );

  const AgentView = activeAgent ? getAgentView(activeAgent.agentKey) : null;

  // Read when the modal opens rather than once at mount: the token is what identifies the user,
  // and it is refreshed underneath a long session.
  const currentUser = isSettingsOpen ? CurrentUser.GetCurrentUserData() : null;

  return (
    <div className="flex h-screen w-full overflow-hidden bg-whitesmoke">
      {hasRail && (
        // Its own width is its own business: on the desktop side it keeps a 64px gutter here and
        // opens over the main area on hover, so nothing in `main` reflows when it does; as a
        // drawer it holds no width at all and is drawn over the screen when it is asked for.
        <AgentRail
          activeKey={effectiveKey}
          onSelect={handleSelect}
          onOpenSettings={handleOpenSettings}
          onSignOut={handleRailSignOut}
          isFromCentral={isFromCentral}
          isSettingsHighlighted={isSettingsHighlighted}
          isDrawer={!isDesktop}
          isDrawerOpen={isDrawerOpen}
          onDismiss={closeDrawer}
        />
      )}

      {/* The way to the rail on a narrow screen, and the only chrome the shell draws itself.
          It stands where AskMila's floating Central button stands on the screens with no rail,
          which is the one corner free on every view: the chat keeps its own menu button at the
          top left, and an agent's header gives its right-hand corner up below `lg`.

          Low in the stack on purpose. AskMila's side panel is an overlay at `z-20` there and
          covers the whole screen when it is open — over this button, which is what should happen:
          a panel is open, and the button behind it is not the thing being pointed at. Every
          dialog on the agent screens outranks it for the same reason. */}
      {hasRail && !isDesktop && (
        <button
          type="button"
          data-testid="rail-launcher"
          onClick={() => setIsDrawerOpen(true)}
          aria-label="AI services"
          aria-expanded={isDrawerOpen}
          aria-haspopup="menu"
          className="fixed right-6 top-8 z-10 grid h-9 w-9 place-items-center rounded-full border border-whitesmoke-500 bg-white text-dark-blue-100 shadow-select transition-colors hover:bg-whitesmoke-600 lg:hidden"
        >
          <LayoutGrid size={18} strokeWidth={1.8} />
        </button>
      )}

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* AskMila stays mounted so switching to an agent and back does not drop a
            conversation in flight — a rail entry is navigation, not a page load. */}
        <div
          data-testid="askmila-pane"
          className={effectiveKey === ASKMILA_KEY ? "h-full" : "hidden"}
        >
          {/* The rail carries the GreenRoad logo, Settings and the Central / sign-out entry
              whenever it is on screen, so AskMila keeps its own copies of those only where
              there is no rail — a user entitled to no agents — where they are still the only
              way to each. Narrow screens used to be the other case; the rail is on them now
              (as a drawer), and two sets of the same chrome on a phone is the one width that
              can least afford it. */}
          <MilaContainer
            handleLogout={handleLogout}
            isFromCentral={isFromCentral}
            registerSignOut={registerSignOut}
            hasRail={hasRail}
            onOpenSettings={handleOpenSettings}
            isSettingsHighlighted={isSettingsHighlighted}
          />
        </div>

        {effectiveKey !== ASKMILA_KEY && (
          // The same frame AskMila uses for its own surface, so an agent and the chat are
          // visibly one product rather than two. Its way back to Central sits at the foot of
          // the rail — an agent is only ever reachable through the rail, so it is always there.
          <div className="relative mx-3 mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-t-3xl bg-white lg:mx-4 lg:mt-4">
            {!activeAgent ? (
              // Landed straight on an agent's address and the entitlement call has not answered
              // yet. The frame is already the right shape, so the wait happens where the agent
              // will be rather than the screen rearranging itself underneath the user.
              <Spinner />
            ) : AgentView ? (
              <Suspense fallback={<Spinner />}>
                <AgentView agent={activeAgent} />
              </Suspense>
            ) : (
              <ComingSoon agent={activeAgent} />
            )}
          </div>
        )}
      </main>

      {/* Outside `main`, so it is one modal over whichever view is up — the chat's or an
          agent's — and never inside the hidden chat pane. */}
      {isSettingsOpen && (
        <UserSettingsModal
          userId={currentUser?.userId}
          ouId={currentUser?.ouId}
          fullName={`${currentUser?.firstName?.trim() || ""} ${currentUser?.lastName?.trim() || ""}`.trim()}
          onClose={handleCloseSettings}
        />
      )}
    </div>
  );
};

export default AppShell;
